import {
  BLUETOOTH_FILTER_PREFIX,
  BLUETOOTH_DC_REASONS,
} from "./deviceProfiles";

export class BluetoothManager {
  device?: BluetoothDevice;
  server?: BluetoothRemoteGATTServer;

  characteristics = new Map<string, BluetoothRemoteGATTCharacteristic>();
  listeners = new Map<string, Function[]>();

  onDisconnect?: (reason: number) => void;

  connected = false;
  reconnectAttempts = 0;
  maxReconnectAttempts = 5;

  private withTimeout<T>(
    promise: Promise<T>,
    ms: number,
    label: string,
  ): Promise<T> {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(
          () => reject(new Error(`Timeout: ${label} took longer than ${ms}ms`)),
          ms,
        ),
      ),
    ]);
  }

  checkSupportBLE(): boolean {
    return !!navigator?.bluetooth;
  }

  setOnDisconnectCallback(callback: (reason: number) => void) {
    this.onDisconnect = callback;
  }

  async requestDevice(services: Record<string, string>) {
    console.log("Requesting Bluetooth Device...");
    const serviceUUIDs = Object.values(services);

    this.device = await navigator.bluetooth.requestDevice({
      filters: [{ namePrefix: BLUETOOTH_FILTER_PREFIX }],
      optionalServices: serviceUUIDs,
    });

    this.device.addEventListener(
      "gattserverdisconnected",
      this.handleDisconnect,
    );

    return this.device;
  }

  async connect() {
    console.log("Connecting to device...");
    if (!this.device) throw new Error("No device selected");

    this.server = await this.device.gatt!.connect();
    this.reconnectAttempts = 0;
    this.connected = true;

    // small delay to ensure connection is fully established before proceeding with setup
    await new Promise((resolve) => setTimeout(resolve, 500));
  }

  async setupServices(
    services: Record<string, string>,
    characteristics: Record<string, Record<string, string>>,
  ) {
    if (!this.server) throw new Error("Not connected");

    console.log("Setting up services...");

    for (const [serviceName, serviceUUID] of Object.entries(services)) {
      console.log(`Setting up service ${serviceName}`);
      const service = await this.withTimeout(
        this.server.getPrimaryService(serviceUUID),
        10000,
        `getPrimaryService(${serviceName})`,
      );

      const serviceChars = characteristics[serviceName];

      for (const [charName, charUUID] of Object.entries(serviceChars)) {
        console.log(`Setting up characteristic ${charName}`);
        const char = await this.withTimeout(
          service.getCharacteristic(charUUID),
          10000,
          `getCharacteristic(${serviceName}.${charName})`,
        );

        const key = `${serviceName}.${charName}`;

        this.characteristics.set(key, char);

        if (char.properties.notify || char.properties.indicate) {
          console.log(`Starting notifications for ${key}`);
          await char.startNotifications();
          console.log(`Subscribed to notifications for ${key}`);
          char.addEventListener(
            "characteristicvaluechanged",
            (event: Event) => {
              const target = event.target as BluetoothRemoteGATTCharacteristic;
              this.emit(key, target?.value || null);
            },
          );
        }
      }
    }
    console.log("All services set up");
  }

  async write(service: string, char: string, data: Uint8Array) {
    const key = `${service}.${char}`;

    const characteristic = this.characteristics.get(key);

    if (!characteristic) {
      throw new Error(`Characteristic ${key} not found`);
    }

    await characteristic.writeValue(data as BufferSource);
  }

  async writeStr(service: string, char: string, data: string) {
    const key = `${service}.${char}`;

    const characteristic = this.characteristics.get(key);

    if (!characteristic) {
      throw new Error(`Characteristic ${key} not found`);
    }

    const encoder = new TextEncoder();
    const encodedData = encoder.encode(data);

    await characteristic.writeValue(encodedData as BufferSource);
  }

  private async readBuff(service: string, char: string, timeout: number = 0) {
    const key = `${service}.${char}`;

    const characteristic = this.characteristics.get(key);

    if (!characteristic) {
      throw new Error(`Characteristic ${key} not found`);
    }

    let buf;
    if (timeout > 0) {
      buf = await this.withTimeout(
        characteristic.readValue(),
        5000,
        `readBuff${service}-${char}`,
      );
    } else {
      buf = await characteristic.readValue();
    }

    return buf;
  }

  async read(service: string, char: string, timeout: number = 0) {
    const buf = await this.readBuff(service, char, timeout);
    const decoder = new TextDecoder();
    return decoder.decode(buf);
  }

  async readBool(service: string, char: string, timeout: number = 0) {
    const buf = await this.readBuff(service, char, timeout);

    const bytes = new Uint8Array(buf.buffer);
    return bytes[0] === 1 ? true : false;
  }

  subscribe(service: string, char: string, cb: Function) {
    const key = `${service}.${char}`;

    if (!this.listeners.has(key)) {
      this.listeners.set(key, []);
    }

    this.listeners.get(key)!.push(cb);
  }

  emit(key: string, value: DataView | null) {
    const subs = this.listeners.get(key) || [];

    subs.forEach((cb) => cb(value));
  }

  handleDisconnect = async () => {
    console.warn("BLE disconnected");
    this.connected = false;

    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;

    this.reconnectAttempts++;

    try {
      await this.connect();
      this.reconnectAttempts = 0;
      console.log("Reconnected");
    } catch (err) {
      setTimeout(this.handleDisconnect, 2000);
    }
  };

  disconnect(reason: number = BLUETOOTH_DC_REASONS.UNKNOWN) {
    console.log("Disconnecting from device...");
    if (this.device?.gatt?.connected) {
      this.device.gatt.disconnect();
    }
    this.connected = false;
    this.device = undefined;
    this.server = undefined;
    this.characteristics.clear();
    this.listeners.clear();
    if (this.onDisconnect) {
      this.onDisconnect(reason);
    }
    console.log("Disconnected");
  }
}
